import { useHelper } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";

import { useModelViewerSettings } from "./useModelViewerSettings";

const POINT_LIGHT_POSITION = new THREE.Vector3(-0.5, 0.5, -0.5);
const DIR_LIGHT_POSITION = new THREE.Vector3(-1, 1, 1);

export const Lights = () => {
  const { showLightSource, lightingIntensity, lightingType } =
    useModelViewerSettings();

  const hemisphereLight = useRef<THREE.HemisphereLight>(null!);
  const directionalLight = useRef<THREE.DirectionalLight>(null!);
  const pointLight = useRef<THREE.PointLight>(null!);

  const [pointLightPos, setPointLightPos] = useState<THREE.Vector3>();
  const [dirLightPos, setDirLightPos] = useState<THREE.Vector3>();

  const { camera } = useThree();

  useFrame(() => {
    if (lightingType === "POINT") {
      // Apply Z to the vector going to origin, then move x,y on that plane
      const toOrigin = camera.worldToLocal(new THREE.Vector3(0, 0, 0));
      const newPointLightPos = toOrigin
        .multiplyScalar(
          (toOrigin.length() + POINT_LIGHT_POSITION.z) / toOrigin.length(),
        )
        .add(
          new THREE.Vector3(POINT_LIGHT_POSITION.x, POINT_LIGHT_POSITION.y, 0),
        );
      setPointLightPos(newPointLightPos);
    } else if (lightingType === "DIRECTIONAL") {
      setDirLightPos(
        camera.worldToLocal(new THREE.Vector3(0, 0, 0)).add(DIR_LIGHT_POSITION),
      );
    }
  });

  useHelper(
    showLightSource && lightingType === "DIRECTIONAL" ? directionalLight : null,
    THREE.DirectionalLightHelper,
    0.5,
  );
  useHelper(
    showLightSource && lightingType === "POINT" ? pointLight : null,
    THREE.PointLightHelper,
    0.5,
  );

  useEffect(() => {
    if (
      !hemisphereLight.current ||
      !directionalLight.current ||
      !pointLight.current
    )
      return;

    const FILL_RATIO = 0.5;

    if (lightingType === "AMBIENT") {
      hemisphereLight.current.intensity = lightingIntensity;
      directionalLight.current.intensity = 0;
      pointLight.current.intensity = 0;
    } else if (lightingType === "DIRECTIONAL") {
      hemisphereLight.current.intensity = lightingIntensity * FILL_RATIO;
      directionalLight.current.intensity = lightingIntensity;
      pointLight.current.intensity = 0;
    } else if (lightingType === "POINT") {
      hemisphereLight.current.intensity = lightingIntensity * FILL_RATIO;
      directionalLight.current.intensity = 0;
      pointLight.current.intensity = lightingIntensity;
    }
  }, [lightingIntensity, lightingType]);

  return (
    <>
      <hemisphereLight ref={hemisphereLight} args={[0xffffff, 0x888888]} />
      <directionalLight ref={directionalLight} position={dirLightPos} />
      <pointLight ref={pointLight} position={pointLightPos} />
    </>
  );
};
